import { decodeJwt } from 'jose';

/** Session token metadata the middleware requires before allowing dashboard routes. */
export function passesOnboardingGate(
  metadata: Partial<CustomJwtSessionClaims['metadata']> | undefined,
): boolean {
  return metadata?.onboardingSeen === true || metadata?.onboardingComplete === true;
}

/** Whether a user needs onboarding, including the Vercel integration exemption. */
export function needsOnboarding(
  metadata: Partial<CustomJwtSessionClaims['metadata']> | undefined,
): boolean {
  return metadata?.source !== 'vercel' && !passesOnboardingGate(metadata);
}

interface TokenSession {
  getToken(options: { skipCache: boolean }): Promise<string | null>;
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Mints fresh session tokens until one carries the onboarding gate claims.
 * `session.reload()` only adopts Clerk's cached last-active token, which predates
 * the server-side metadata write, so navigating after it bounces back to onboarding.
 */
export async function refreshOnboardingGateClaims(
  session: TokenSession,
  { attempts = 4, delayMs = 500, sleep = wait } = {},
): Promise<void> {
  for (let attempt = 1; attempt <= attempts; attempt++) {
    const token = await session.getToken({ skipCache: true });
    if (token && passesOnboardingGate(decodeJwt<CustomJwtSessionClaims>(token).metadata)) return;
    if (attempt < attempts) await sleep(delayMs * attempt);
  }
  throw new Error('Session token is missing onboarding claims');
}
